'use client';

import * as React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { cx } from '../utils';
import type { UnsafeProps } from '../escape-hatches';

/* ═══════════════════════════════════════════════════
   Radio + RadioGroup — mutually exclusive selection
   APG: Radio Group (roving tabindex + arrow-key navigation)
   ═══════════════════════════════════════════════════ */

/* ── RadioGroup context ─────────────────────────── */

interface RadioGroupContextValue {
  value: string;
  onChange: (value: string) => void;
  size: 'sm' | 'md';
  disabled: boolean;
  name: string;
  /** Returns true if this is the first radio to register (used for tabIndex fallback). */
  claimFirst: () => boolean;
}

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(null);

/* ── RadioGroup ─────────────────────────────────── */

export interface RadioGroupProps extends UnsafeProps {
  /** Current selected value. Controlled. */
  value: string;
  /** Called when selection changes. */
  onChange: (value: string) => void;
  /** Accessible label for the group. */
  ariaLabel?: string;
  /** Layout direction. @default 'horizontal' */
  orientation?: 'horizontal' | 'vertical';
  /** Visual size for all radios in the group. @default 'md' */
  size?: 'sm' | 'md';
  /** Disables all radios in the group. */
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup(
  {
    value,
    onChange,
    ariaLabel,
    orientation = 'horizontal',
    size = 'md',
    disabled = false,
    className,
    style,
    unsafe_className,
    unsafe_style,
    children,
  },
  ref,
) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const mergedRef = React.useMemo(() => {
    return (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    };
  }, [ref]);

  /* Stable name for the radio group so native inputs share a name attribute. */
  const groupName = React.useId();

  /* Track which radio is "first" for tabIndex fallback when none is selected. */
  const claimedRef = React.useRef(false);
  claimedRef.current = false; // reset each render
  const claimFirst = React.useCallback(() => {
    if (!claimedRef.current) {
      claimedRef.current = true;
      return true;
    }
    return false;
  }, []);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      /* Per APG Radio Group pattern, both arrow axes work regardless of
         orientation — ArrowRight/ArrowDown move forward, ArrowLeft/ArrowUp
         move backward. */
      const isNext = e.key === 'ArrowRight' || e.key === 'ArrowDown';
      const isPrev = e.key === 'ArrowLeft' || e.key === 'ArrowUp';

      if (!isNext && !isPrev) return;

      e.preventDefault();

      if (!containerRef.current) return;
      const radios = Array.from(
        containerRef.current.querySelectorAll<HTMLInputElement>(
          'input[type="radio"]:not(:disabled)',
        ),
      );
      if (radios.length === 0) return;

      const currentIdx = radios.findIndex((r) => r === document.activeElement);
      let nextIdx: number;

      if (isNext) {
        nextIdx = currentIdx === -1 || currentIdx >= radios.length - 1 ? 0 : currentIdx + 1;
      } else {
        nextIdx = currentIdx <= 0 ? radios.length - 1 : currentIdx - 1;
      }

      const nextRadio = radios[nextIdx];
      nextRadio.focus();
      const nextValue = nextRadio.getAttribute('data-value');
      if (nextValue != null) {
        onChange(nextValue);
      }
    },
    [onChange],
  );

  const ctxValue = React.useMemo<RadioGroupContextValue>(
    () => ({
      value,
      onChange,
      size,
      disabled,
      name: groupName,
      claimFirst,
    }),
    [value, onChange, size, disabled, groupName, claimFirst],
  );

  return (
    <RadioGroupContext.Provider value={ctxValue}>
      {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus -- focus is managed by roving tabindex on child radios, not the container */}
      <div
        ref={mergedRef}
        role="radiogroup"
        aria-label={ariaLabel}
        aria-orientation={orientation}
        className={cx(
          'j-radio-group',
          orientation === 'vertical' && 'j-radio-group-vertical',
          className,
          unsafe_className,
        )}
        style={unsafe_style ? { ...style, ...unsafe_style } : style}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
});

/* ── Radio ──────────────────────────────────────── */

export interface RadioProps extends UnsafeProps {
  /** Value identifier within the group. */
  value: string;
  /** Visible label. */
  label?: ReactNode;
  /** Disabled state (overridden by group disabled). */
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { value, label, disabled: itemDisabled, className, style, unsafe_className, unsafe_style },
  forwardedRef,
) {
  const ctx = React.useContext(RadioGroupContext);

  if (!ctx) {
    throw new Error('<Radio> must be used within a <RadioGroup>.');
  }

  const { value: groupValue, onChange, size, disabled: groupDisabled, name, claimFirst } = ctx;

  const isChecked = groupValue === value;
  const isDisabled = groupDisabled || !!itemDisabled;

  // Roving tabindex: selected radio gets tabIndex=0. If no radio matches
  // the group value, the first radio to render gets tabIndex=0 as the
  // keyboard entry point (claimFirst returns true only once per render cycle).
  const hasSelection = groupValue !== '' && groupValue != null;
  const isFirst = claimFirst();
  const tabIndex = isChecked || (!hasSelection && isFirst) ? 0 : -1;

  const handleChange = React.useCallback(() => {
    if (!isDisabled) {
      onChange(value);
    }
  }, [isDisabled, onChange, value]);

  return (
    <label
      className={cx('j-radio-label', size === 'sm' && 'j-radio-sm', className, unsafe_className)}
      style={unsafe_style ? { ...style, ...unsafe_style } : style}
    >
      <input
        ref={forwardedRef}
        type="radio"
        name={name}
        value={value}
        data-value={value}
        checked={isChecked}
        disabled={isDisabled}
        tabIndex={tabIndex}
        onChange={handleChange}
      />
      <span className="j-radio-circle" data-checked={isChecked ? 'true' : 'false'}>
        {isChecked && <span className="j-radio-dot" />}
      </span>
      {label != null && <span>{label}</span>}
    </label>
  );
});
